/**
 * The stored ground truth follows the human save.
 *
 * `publishGraph` (store) dispatches `GRAPH_SAVED_EVENT` once a save has landed;
 * EditorWrapper listens, because it is the one component that knows the rendered
 * page size the GoldDrawing export needs. These helpers hold the decisions so
 * they can be tested without mounting the editor.
 */

import { GRAPH_SAVED_EVENT, type GraphSavedDetail } from '@/store/graphSavedEvent'

export { GRAPH_SAVED_EVENT }
export type { GraphSavedDetail }

export function isGraphSavedDetail(value: unknown): value is GraphSavedDetail {
  if (value === null || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return typeof v.graphId === 'string' && typeof v.version === 'number'
}

/**
 * Store only when the save is for the graph on screen, a human has edited it
 * (version 1 is the machine parse — VISN-319), and the page has rendered so the
 * export's geometry is in a known coordinate space.
 */
export function shouldStoreGroundTruth(
  detail: GraphSavedDetail,
  currentGraphId: string | undefined,
  imageSize: { width: number; height: number } | null,
): boolean {
  if (!currentGraphId || detail.graphId !== currentGraphId) return false
  if (detail.version <= 1) return false
  return imageSize !== null && imageSize.width > 0 && imageSize.height > 0
}

/**
 * `<drawing_key>.labelled.json` — the name the eval's fixture loader expects. The
 * key is already extension-free: the editor strips it before building the export.
 */
export function groundTruthFilename(payload: Record<string, unknown>): string {
  const key = typeof payload.drawing_key === 'string' && payload.drawing_key ? payload.drawing_key : 'ground-truth'
  return `${key}.labelled.json`
}
